
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ROICalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState<string>('10000');
  const [finalValue, setFinalValue] = useState<string>('12000');
  const [additionalCosts, setAdditionalCosts] = useState<string>('0');
  const [timeHeld, setTimeHeld] = useState<string>('1');
  const [roi, setROI] = useState<number | null>(null);
  const [annualizedROI, setAnnualizedROI] = useState<number | null>(null);
  const [totalGain, setTotalGain] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateROI = () => {
    try {
      setError(null);
      
      const initial = parseFloat(initialInvestment);
      const final = parseFloat(finalValue);
      const costs = parseFloat(additionalCosts) || 0;
      const time = parseFloat(timeHeld);

      if (isNaN(initial) || initial <= 0) {
        setError("Please enter a valid initial investment amount");
        return;
      }
      if (isNaN(final) || final < 0) {
        setError("Please enter a valid final value");
        return;
      }
      if (isNaN(time) || time <= 0) {
        setError("Please enter a valid time period");
        return;
      }

      const totalCost = initial + costs;
      const gain = final - totalCost;
      const roiPercentage = (gain / totalCost) * 100;
      const annualizedROIPercentage = (Math.pow(final / totalCost, 1 / time) - 1) * 100;

      setROI(roiPercentage);
      setAnnualizedROI(annualizedROIPercentage);
      setTotalGain(gain);

    } catch (err) {
      setError("Error calculating ROI. Please check your inputs.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">ROI (Return on Investment) Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="initial">Initial Investment ($)</Label>
              <Input
                id="initial"
                type="number"
                value={initialInvestment}
                onChange={(e) => setInitialInvestment(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="final">Final Value ($)</Label>
              <Input
                id="final"
                type="number"
                value={finalValue}
                onChange={(e) => setFinalValue(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="costs">Additional Costs ($)</Label>
              <Input
                id="costs"
                type="number"
                value={additionalCosts}
                onChange={(e) => setAdditionalCosts(e.target.value)}
                className="mt-1"
                placeholder="Transaction fees, taxes, etc."
              />
            </div>

            <div>
              <Label htmlFor="time">Time Held (Years)</Label>
              <Input
                id="time"
                type="number"
                step="0.1"
                value={timeHeld}
                onChange={(e) => setTimeHeld(e.target.value)}
                className="mt-1"
              />
            </div>

            <Button onClick={calculateROI} className="w-full">
              Calculate ROI
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {roi !== null && (
              <div className="space-y-4 mt-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium">Total ROI</h3>
                  <p className={`text-2xl font-bold ${roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {roi >= 0 ? '+' : ''}{roi.toFixed(2)}%
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-medium">Annualized ROI</h3>
                  <p className={`text-xl font-bold ${annualizedROI! >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {annualizedROI! >= 0 ? '+' : ''}{annualizedROI!.toFixed(2)}%
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium">Total Gain/Loss</h3>
                  <p className={`text-xl font-bold ${totalGain! >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalGain! >= 0 ? '+$' : '-$'}{Math.abs(totalGain!).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ROICalculator;
